// Resource.Reference — Include references, planned placeholders, and the
// acyclic-containment invariant (generation phase 1 step 2).
//
// Generation design v2.1 §1.2 / §1.5 / §2 (`.claude/analysis/incremental-
// generation-2026-10-01/design.md`), IDD 24123ec9 + 45cad25b + 9c0474cb:
// a material is a resource composed of REFERENCES to resources.
//
// **Include.** An Include reference is a node in the referrer's content
// tree (`type: "include"`), so it keeps its position and can sit between
// prose. It pins `(resourceId, version)` of a COMMITTED version (a
// `Resource.Ref`, whole or part). Its `refId` is the node's element id.
// Any resource kind is includable: another Material (module, quiz), a
// Source (an embedded course book), a File, an Export. Reorder is the
// existing `move_element` on the node; adding and removing are
// `include_resource` / `exclude_resource`, each a committed step of the
// referrer (the design's "referrer, new version"). The edit primitives
// may move or remove an include node but never create or retarget one,
// so every include passes the checks below.
//
// **Policy** (`follow | ask`) is lineage-level metadata on the referrer
// (`Lineage.referencePolicies`), not part of any version.
//
// **Freshness + FOLLOW advance** (step 3). A pin is Behind when it is not
// a head of its target's committed DAG (an uncommitted draft never makes
// anything Behind). `advance_reference` moves one pin forward to a newer
// committed version: a committed step of the referrer, like include.
// `pendingFollowAdvances` says which FOLLOW pins on a just-committed
// target advance (single newer head only; several heads = a human
// choice). The shell issues them, because the command carries the
// referrer's new content hash and core never hashes (exec decision 21).
// ASK pins never advance; `newerVersionsAvailable` is their notice. The
// author answers it with `advance_reference` ("use this version", any
// newer head) or `decline_newer_versions` ("keep this version"): the
// heads declined are lineage metadata (`Lineage.declinedNewerVersions`,
// no version is made), and the notice stays hidden until a head appears
// that was not declined. Advancing the pin clears its declines.
//
// **Acyclic containment.** The Include graph over resources is acyclic.
// Its edges are the include nodes in every Material's working draft (and
// in a deleted Material's last draft, so restoring it cannot close a
// loop). Pinned versions alone could never loop (a version only pins
// versions committed before it); the invariant is about RESOURCES, which
// is what the FOLLOW cascade and the tree view walk. Checked on include
// and on every whole-draft replacement that can bring includes back
// (check-out, restore).
//
// **Planned placeholder.** `plan_resource` births a Material whose v1 is
// the empty placeholder and whose `plan` (kind + brief) says what it is
// meant to be. It is a real resource from the start, so it can be
// included at v1 and keeps its id when content lands (a commit makes
// v2). "Planned" as seen from a reference = the pinned version is the
// planned v1 (`isPlannedVersion`).
//
// **Deletion.** Deleting a referenced resource is allowed; the include
// stays and resolves to `IncludeTarget.Removed` (graceful degradation,
// shown as "removed resource"). Including a deleted resource is refused.
//
// Export resolution (phase 6) lives in `resolveIncludes.ts`: packaging
// inlines each included part at its pinned version and flags the rest.
export { asksForContent, findIncludeCycle, INCLUDE_ELEMENT_TYPE, includeEdges, includesIn, includesOfContent, includeTarget, includeTargetOf, isPlannedVersion, isStillPlanned, livePartsOf, mkAdvanceReference, mkBrief, mkDeclineNewerVersions, mkExcludeResource, mkIncludeCycle, mkIncludeElement, mkIncludeElementEditRefused, mkIncludeResource, mkPlan, mkPlanResource, mkReferenceNotBehind, mkReferenceNotFound, mkReferenceTargetNotFound, mkReferenceTargetVersionNotFound, mkResourcePlanned, mkResourceReferenceAdvanced, mkResourceReferenceExcluded, mkResourceReferenceIncluded, mkResourceReferenceNewerDeclined, newerHeads, newerVersionsAvailable, pendingFollowAdvances, placeholderContent, retargetInclude, };
import { exhaustSwitchCase, Maybe } from "@bilda/utils";
import * as Version from "../versioning/version.js";
import * as Element from "./element.js";
import * as Id from "./id.js";
import * as Lineage from "./lineage.js";
import * as Ref from "./ref.js";
const mkBrief = (s) => s;
const mkPlan = (kind, brief, objectiveIds = []) => ({
    kind,
    brief,
    ...(objectiveIds.length > 0 ? { objectiveIds } : {}),
});
// The planned v1 content: an empty element tree. The plan itself is
// metadata on the Material, so every placeholder has the same bytes.
const placeholderContent = Element.serializeContentTree([]);
// === The include node (stored element) ===
const INCLUDE_ELEMENT_TYPE = "include";
const mkIncludeElement = (refId, target) => ({
    id: refId,
    type: INCLUDE_ELEMENT_TYPE,
    attrs: {
        resourceId: Id.from(target.id),
        version: Version.from(target.version),
        ...(target.type === "part_resource_ref"
            ? { locator: `${target.locator}` }
            : {}),
    },
});
// Parse an element as an include node; anything else (or a malformed
// include) is Nothing, so readers degrade instead of failing.
const includeTargetOf = (el) => {
    if (el.type !== INCLUDE_ELEMENT_TYPE)
        return Maybe.nothing;
    const rawId = el.attrs?.resourceId;
    const rawVersion = el.attrs?.version;
    const rawLocator = el.attrs?.locator;
    if (typeof rawId !== "string" || typeof rawVersion !== "number") {
        return Maybe.nothing;
    }
    const id = Id.to(rawId);
    const version = Version.to(rawVersion);
    if (id._tag === "Result.Err" || version._tag === "Result.Err") {
        return Maybe.nothing;
    }
    return Maybe.just(typeof rawLocator === "string" && rawLocator.length > 0
        ? Ref.mkPart(id.value, version.value, Ref.mkElementLocator(rawLocator))
        : Ref.mkWhole(id.value, version.value));
};
const includesIn = (tree, prefix = []) => tree.flatMap((el) => {
    const path = [...prefix, el.id];
    const here = includeTargetOf(el);
    const self = here._tag === "Maybe.Just"
        ? [{ refId: el.id, path, target: here.value }]
        : [];
    return [...self, ...includesIn(el.children ?? [], path)];
});
const includesOfContent = (content) => Maybe.match({
    just: (tree) => includesIn(tree),
    nothing: () => [],
})(Element.parseContentTree(content));
// The parts a container holds: its includes, minus those whose target is
// deleted (#466). A deleted part keeps its include in the container's
// content, so Restore puts it back exactly where it was; while it is in
// Trash, no structure lists it (knowledge-bank.md §7: a link to a deleted
// resource is dropped at read time). Every read of a container's parts
// (its fit, the outline, the assistant's map, export) goes through this; the
// raw `includesOfContent` stays for what is about the content itself (edits,
// cycles). An id the workspace does not know at all stays: that is a broken
// log, shown as such, never hidden.
const livePartsOf = (resources, content) => includesOfContent(content).filter((i) => resources.get(i.target.id)?.type !== "deleted");
// === Projections ===
// The resources a resource includes (its outgoing Include edges). A
// deleted Material keeps its last draft's edges (see header).
const includeEdges = (resource) => {
    const live = resource.type === "deleted" ? resource.prior : resource;
    return live.type === "material"
        ? includesOfContent(live.content).map((i) => i.target.id)
        : [];
};
// A path of Include edges `from` →* `to` (both ends included), if any.
const includePath = (state, from, to) => {
    const visited = new Set();
    const walk = (at) => {
        if (at === to)
            return [at];
        if (visited.has(at))
            return undefined;
        visited.add(at);
        const resource = state.resources.get(at);
        if (resource === undefined)
            return undefined;
        for (const next of includeEdges(resource)) {
            const rest = walk(next);
            if (rest !== undefined)
                return [at, ...rest];
        }
        return undefined;
    };
    return walk(from);
};
// Would `referrer` including each of `targets` close a loop? Returns the
// first cycle found as an error value (referrer → … → referrer).
const findIncludeCycle = (state, referrer, targets) => {
    for (const target of targets) {
        const path = includePath(state, target, referrer);
        if (path !== undefined) {
            return Maybe.just(mkIncludeCycle(referrer, target, [referrer, ...path]));
        }
    }
    return Maybe.nothing;
};
const includeTarget = (state, ref) => {
    const resource = state.resources.get(ref.id);
    return resource === undefined || resource.type === "deleted"
        ? { _tag: "IncludeTarget.Removed", resourceId: ref.id }
        : { _tag: "IncludeTarget.Available", resource };
};
// The pinned version is a planned resource's placeholder: its content is
// still the planned v1's (a later version may only have changed builds-on).
const isPlannedVersion = (material, version) => asksForContent(material) &&
    hashOf(material, version) === hashOf(material, Version.v1);
const hashOf = (material, version) => material.lineage.versions.find((i) => i.version === version)?.contentHash;
// A plan asks for content unless it only names a container preset: a module
// or course with no brief, as the Studio makes one (`Container.
// mkContainerPlan`). Such a container is made of its parts, so one with none
// left is an empty container, never a queued part. (An agent's planned module
// carries a brief and is written by generation.)
const asksForContent = (material) => {
    const plan = material.plan;
    if (plan === undefined)
        return false;
    switch (plan.kind) {
        case "course":
        case "module":
            return plan.brief !== "";
        case "chapter":
        case "page":
        case "quiz":
        case "handout":
            return true;
        default:
            return exhaustSwitchCase(plan.kind);
    }
};
// The working draft is still the planned placeholder (nothing generated or
// written yet).
const isStillPlanned = (material) => asksForContent(material) &&
    material.contentHash === hashOf(material, Version.v1);
// === Freshness + FOLLOW (step 3) ===
// Re-pin the include node `refId` to `target`. Element ids are unique
// within a tree, so a map over it is enough.
const retargetInclude = (tree, refId, target) => tree.map((el) => el.id === refId
    ? mkIncludeElement(refId, target)
    : el.children === undefined
        ? el
        : {
            ...el,
            children: retargetInclude(el.children, refId, target),
        });
// The committed heads that descend from a pinned version: empty when the
// pin is itself a head (Fresh) or the target is not a live Material.
const newerHeads = (state, pin) => {
    const target = state.resources.get(pin.id);
    if (target === undefined || target.type !== "material")
        return [];
    return Lineage.heads(target.lineage).filter((h) => Lineage.isStrictAncestor(target.lineage, pin.version, h));
};
const newerVersionsAvailable = (state, referrer) => includesOfContent(referrer.content).flatMap((i) => {
    const heads = newerHeads(state, i.target);
    const declined = referrer.lineage.declinedNewerVersions.get(i.refId) ?? [];
    return heads.every((h) => declined.includes(h))
        ? []
        : [{ refId: i.refId, pinned: i.target, heads }];
});
// Every FOLLOW include on `target` that should advance now. Only a whole
// reference with a single newer head moves (a part reference's locator
// may not exist in the newer version; several heads are a human choice).
// A referrer with uncommitted edits is skipped: the advance commits the
// referrer, and it must never commit the author's draft on their behalf
// (MVP shortcut: that pin stays Behind and shows as a notice).
const pendingFollowAdvances = (state, target) => [...state.resources.values()].flatMap((r) => {
    if (r.type !== "material")
        return [];
    if (Lineage.hasUncommittedChanges(r.lineage, r.contentHash))
        return [];
    const tree = Element.parseContentTree(r.content);
    if (tree._tag === "Maybe.Nothing")
        return [];
    return includesIn(tree.value).flatMap((i) => {
        if (i.target.id !== target)
            return [];
        if (i.target.type !== "whole_resource_ref")
            return [];
        if (r.lineage.referencePolicies.get(i.refId) !== "follow") {
            return [];
        }
        const heads = newerHeads(state, i.target);
        const head = heads[0];
        if (heads.length !== 1 || head === undefined)
            return [];
        const to = Ref.mkWhole(target, head);
        return [
            {
                referrer: r,
                refId: i.refId,
                to,
                content: Element.serializeContentTree(retargetInclude(tree.value, i.refId, to)),
            },
        ];
    });
});
const mkPlanResource = (resourceId, title, plan, classification, templateRef, provenance, contentHash, originatingMessageId, buildsOn = []) => ({
    type: "plan_resource",
    resourceId,
    title,
    plan,
    classification,
    templateRef,
    provenance,
    contentHash,
    originatingMessageId,
    buildsOn,
});
const mkIncludeResource = (resourceId, baseVersion, expectedContentHash, parentPath, position, refId, target, policy, contentHash, originatingMessageId) => ({
    type: "include_resource",
    resourceId,
    baseVersion,
    expectedContentHash,
    parentPath,
    position,
    refId,
    target,
    policy,
    contentHash,
    originatingMessageId,
});
const mkExcludeResource = (resourceId, baseVersion, expectedContentHash, refId, contentHash, originatingMessageId) => ({
    type: "exclude_resource",
    resourceId,
    baseVersion,
    expectedContentHash,
    refId,
    contentHash,
    originatingMessageId,
});
const mkAdvanceReference = (resourceId, baseVersion, expectedContentHash, refId, toVersion, contentHash, originatingMessageId) => ({
    type: "advance_reference",
    resourceId,
    baseVersion,
    expectedContentHash,
    refId,
    toVersion,
    contentHash,
    originatingMessageId,
});
const mkDeclineNewerVersions = (resourceId, refId) => ({
    type: "decline_newer_versions",
    resourceId,
    refId,
});
const mkResourcePlanned = (resourceId, title, plan, classification, content, contentHash, templateRef, provenance, originatingMessageId, buildsOn = []) => ({
    type: "resource_planned",
    resourceId,
    title,
    plan,
    classification,
    content,
    contentHash,
    templateRef,
    provenance,
    originatingMessageId,
    ...(buildsOn.length > 0 ? { buildsOn } : {}),
});
const mkResourceReferenceIncluded = (resourceId, refId, parentPath, position, target, policy, contentHashBefore, contentHash, originatingMessageId) => ({
    type: "resource_reference_included",
    resourceId,
    refId,
    parentPath,
    position,
    target,
    policy,
    contentHashBefore,
    contentHash,
    originatingMessageId,
});
const mkResourceReferenceExcluded = (resourceId, refId, path, target, contentHashBefore, contentHash, originatingMessageId) => ({
    type: "resource_reference_excluded",
    resourceId,
    refId,
    path,
    target,
    contentHashBefore,
    contentHash,
    originatingMessageId,
});
const mkResourceReferenceAdvanced = (resourceId, refId, from, to, contentHashBefore, contentHash, originatingMessageId) => ({
    type: "resource_reference_advanced",
    resourceId,
    refId,
    from,
    to,
    contentHashBefore,
    contentHash,
    originatingMessageId,
});
const mkResourceReferenceNewerDeclined = (resourceId, refId, pinned, heads) => ({
    type: "resource_reference_newer_declined",
    resourceId,
    refId,
    pinned,
    heads,
});
const mkReferenceTargetNotFound = (resourceId, targetId) => ({
    type: "reference_target_not_found",
    resourceId,
    targetId,
});
const mkReferenceTargetVersionNotFound = (resourceId, target) => ({
    type: "reference_target_version_not_found",
    resourceId,
    target,
});
const mkIncludeCycle = (resourceId, targetId, cycle) => ({
    type: "include_cycle",
    resourceId,
    targetId,
    cycle,
});
const mkReferenceNotFound = (resourceId, refId) => ({
    type: "reference_not_found",
    resourceId,
    refId,
});
const mkIncludeElementEditRefused = (resourceId, refId) => ({
    type: "include_element_edit_refused",
    resourceId,
    refId,
});
const mkReferenceNotBehind = (resourceId, refId, pinned, toVersion) => ({
    type: "reference_not_behind",
    resourceId,
    refId,
    pinned,
    toVersion,
});
//# sourceMappingURL=reference.js.map